import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * GridLayer — Breathing grid with occasional sparkling nodes
 * Enhanced from original HeroBackground with subtle motion
 */

const CELL = 72;
const SPARKLE_COUNT = 8;

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function GridLayer() {
  const sparkles = useMemo(() => {
    const rand = mulberry32(11);
    return Array.from({ length: SPARKLE_COUNT }).map((_, i) => ({
      x: Math.floor(rand() * 20) * CELL + CELL,
      y: Math.floor(rand() * 12) * CELL + CELL,
      dur: 3 + rand() * 2,
      delay: rand() * 5,
      size: 2 + rand() * 2,
    }));
  }, []);

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 1 }}
      aria-hidden="true"
    >
      <style>{`
        @keyframes grid-breathe {
          0%, 100% { opacity: 0.04; }
          50% { opacity: 0.06; }
        }
        @keyframes sparkle-glow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        
        .grid-breathing {
          animation: grid-breathe 8s ease-in-out infinite;
        }
        .sparkle-node {
          animation: sparkle-glow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Breathing grid */}
      <div
        className="grid-breathing absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(102, 54, 157, 0.15) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: `${CELL}px ${CELL}px`,
          maskImage:
            "radial-gradient(ellipse 85% 75% at 50% 50%, black 45%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 85% 75% at 50% 50%, black 45%, transparent 100%)",
        }}
      />

      {/* Sparkling intersection nodes */}
      {sparkles.map((s, i) => (
        <span
          key={i}
          className="sparkle-node absolute rounded-full"
          style={{
            left: s.x,
            top: s.y,
            width: s.size,
            height: s.size,
            background: "#C9A84C",
            boxShadow: "0 0 10px rgba(201, 168, 76, 0.6)",
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
          }}
        />
      ))}

      {/* Vignette overlay for focus */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 50% 50%, rgba(8, 6, 15, 0.4) 0%, transparent 70%),
            linear-gradient(to bottom, rgba(8, 6, 15, 0) 60%, rgba(8, 6, 15, 0.9) 100%)
          `,
        }}
      />
    </div>
  );
}
